// ink-component
import React from 'react';
import { ThemeProvider } from 'styled-components';
import getGlobalProps from '../../helpers/constants/global-props';
import generateRandomId from '../../helpers/generateRandomId';
import {
  LoaderBackdrop,
  LoaderBody,
  LoaderShape,
  LoaderShapePath,
  OverlayLoader,
  StyledLoader,
  WrapperLoader,
} from './styles';

export interface LoaderProps {
  /** Defines if the component is visible */
  isLoading?: any;
  /** Type modifier */
  type?: 'overlay' | 'page' | 'component';
  /** Accessibility text to help loader states be read out by screen readers.[Learn more about accessible texts](/accessibility/aria#alt-text-and-title) */
  altText?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const globalProps = getGlobalProps({});

/**
 * @purpose An animated spinner indicating data is loading
 * @categories accessory
 */
function Loader({ type = 'component', isLoading, altText, ...props }: LoaderProps) {
  const svgPath =
    'M-5 -61a10 10 0 0 1 10 0l45.425625842204 26.226497308104a10 10 0 0 1 5 8.6602540378444l0 52.452994616207a10 10 0 0 1 -5 8.6602540378444l-45.425625842204 26.226497308104a10 10 0 0 1 -10 0l-45.425625842204 -26.226497308104a10 10 0 0 1 -5 -8.6602540378444l0 -52.452994616207a10 10 0 0 1 5 -8.6602540378444 Z';

  const Loader = type === 'overlay' ? OverlayLoader : WrapperLoader;

  const loadingId = `${generateRandomId()}-loader`;

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        active: isLoading,
        type,
      })}
    >
      <Loader {...globalProps} data-testid={props['data-testid']}>
        <StyledLoader>
          <LoaderBackdrop />
          <LoaderBody>
            <LoaderShape
              role="img"
              viewBox="-70 -70 140 140"
              xmlns="http://www.w3.org/2000/svg"
              aria-labelledby={loadingId}
            >
              <title id={loadingId}>{altText || 'loading'}</title>
              <LoaderShapePath d={svgPath} />
              <LoaderShapePath d={svgPath} />
              <LoaderShapePath d={svgPath} />
            </LoaderShape>
          </LoaderBody>
        </StyledLoader>
      </Loader>
    </ThemeProvider>
  );
}

Loader.defaultProps = {
  isLoading: false,
  type: 'component',
  'data-testid': undefined,
};

Loader.displayName = 'Loader';

export default Loader;
